<template>
  <div class="timeline-events-table">
    <div class="table-responsive">
      <table class="table table-bordered table-hover">
        <thead class="thead-light timeline-table-header">
        <tr>
          <th v-if="showDragHandle" class="col-drag"></th>
          <th class="col-index">#</th>
          <th class="col-media">
            Media Assignment
            <span v-if="!isVttMode" class="has-tooltip ml-1"
                  data-tooltip="Select media type (Image, MP3, MP4, or Text) and choose a file. Once a file is selected, the media type cannot be changed.">
              <i class="fa fa-info-circle info-icon"></i>
            </span>
          </th>
          <th class="col-time-range">
            Time Range
            <span class="has-tooltip ml-1"
                  data-tooltip="Format: MM:SS (e.g., 1:30) or seconds (e.g., 90). Start time and end time for this event. Check 'Until End' to make it last until the animation ends.">
              <i class="fa fa-info-circle info-icon"></i>
            </span>
          </th>
          <th class="col-group-easing">
            Group & Easing
            <span class="has-tooltip ml-1"
                  data-tooltip="CSS class identifier and easing effects when event starts (Easing In) and ends (Easing Out)">
              <i class="fa fa-info-circle info-icon"></i>
            </span>
          </th>
          <th class="col-actions"></th>
        </tr>
        </thead>
        <tbody v-if="events.length === 0">
        <tr>
          <td :colspan="showDragHandle ? 6 : 5" class="text-center text-muted">
            {{ emptyMessage }}
          </td>
        </tr>
        </tbody>
        <draggable
            v-else
            v-model="eventsModel"
            class="draggable-container"
            v-bind="dragOptions"
            @start="onDragStart"
            @end="onDragEnd"
            item-key="id"
        >
          <template #item="{ element: event, index }">
            <tbody
                class="timeline-event-group"
                :class="{ 'is-dragging': dragging }"
                :data-event-id="event.id"
            >
              <timeline-event-row
                  :event="event"
                  :index="index"
                  :is-dragging="dragging"
                  :show-drag-handle="showDragHandle"
                  :is-vtt-mode="isVttMode"
                  :event-duration="getEventDuration(event)"
                  :is-end-time-before-start="isEndTimeBeforeStartTime(event)"
                  :show-captions="expandedCaptions[event.id] || false"
                  @set-media-type="(type) => emit('set-media-type', index, type)"
                  @format-time="(field) => emit('format-time', index, field)"
                  @until-end-change="emit('until-end-change', index)"
                  @update-field="(field, value) => emit('update-field', index, field, value)"
                  @remove="emit('remove', index)"
                  @upload-vtt="emit('upload-vtt', index)"
                  @clear-vtt="emit('clear-vtt', index)"
                  @format-caption-time="(captionIndex, field) => emit('format-caption-time', index, captionIndex, field)"
                  @update-caption="(captionIndex, field, value) => emit('update-caption', index, captionIndex, field, value)"
                  @remove-caption="(captionIndex) => emit('remove-caption', index, captionIndex)"
                  @add-caption="emit('add-caption', index)"
                  @toggle-captions="toggleCaptionsForEvent(event.id)"
                  @update-captions="(captions) => emit('update-captions', index, captions)"
              />
            </tbody>
          </template>
        </draggable>
      </table>
    </div>
  </div>
</template>

<script setup>
import {ref, computed} from 'vue'
import draggable from 'vuedraggable'
import TimelineEventRow from './TimelineEventRow.vue'
import {timeToSeconds} from '../../common/time-utils'

const props = defineProps({
  events: {
    type: Array,
    required: true
  },
  showDragHandle: {
    type: Boolean,
    default: true
  },
  isVttMode: {
    type: Boolean,
    default: false
  },
  emptyMessage: {
    type: String,
    default: 'No timeline events added yet. Click "Add" button below to create your first event.'
  }
})

const emit = defineEmits(['update:events', 'set-media-type', 'format-time', 'until-end-change', 'update-field', 'remove', 'upload-vtt', 'clear-vtt', 'format-caption-time', 'update-caption', 'remove-caption', 'add-caption', 'update-captions'])

const dragging = ref(false)
const expandedCaptions = ref({})

const eventsModel = computed({
  get() {
    return props.events
  },
  set(value) {
    emit('update:events', value)
  }
})

const dragOptions = computed(() => ({
  animation: 200,
  group: props.isVttMode ? 'vtt-events' : 'timeline-events',
  disabled: false,
  ghostClass: 'ghost',
  handle: '.drag-handle'
}))

const onDragStart = () => {
  dragging.value = true
}

const onDragEnd = () => {
  dragging.value = false
}

const getEventDuration = (event) => {
  if (!event || event.untilEnd) {
    return 0
  }

  const startTime = timeToSeconds(event.startTime)
  const endTime = timeToSeconds(event.endTime)

  if (startTime < 0 || endTime < 0 || endTime <= startTime) {
    return 0
  }

  return endTime - startTime
}

const isEndTimeBeforeStartTime = (event) => {
  if (!event || event.untilEnd) {
    return false
  }

  const startTime = timeToSeconds(event.startTime)
  const endTime = timeToSeconds(event.endTime)

  return startTime >= 0 && endTime >= 0 && endTime < startTime
}

const toggleCaptionsForEvent = (eventId) => {
  expandedCaptions.value[eventId] = !expandedCaptions.value[eventId]
}
</script>

<style lang="scss" scoped>
.timeline-events-table {
  .table-responsive {
    overflow: visible !important;
  }

  .table {
    background-color: #fff;
    overflow: visible;

    thead.timeline-table-header {
      background-color: #f8f9fa;
      border-bottom: 2px solid #dee2e6;

      th {
        font-weight: 600;
        font-size: 0.9rem;
        padding: 0.75rem 0.5rem;
        text-align: left;
        color: #495057;
        border-bottom: 2px solid #dee2e6;
        white-space: nowrap;
        position: relative;
        overflow: visible;

        small {
          font-weight: normal;
          display: block;
          margin-top: 4px;
          font-size: 0.75rem;
          color: #6c757d;
          line-height: 1.2;
        }

        &.col-drag {
          width: 3%;
          text-align: center;
          min-width: 30px;
          border-right: none;
        }

        &.col-index {
          width: 5%;
          text-align: center;
          border-left: none;
        }

        &.col-media {
          width: 45%;
          min-width: 250px;
        }

        &.col-time-range {
          width: 30%;
          min-width: 280px;
        }

        &.col-group-easing {
          width: 25%;
          min-width: 200px;
        }

        &.col-actions {
          text-align: center;
          min-width: 40px;
        }
      }
    }

    tbody {
      tr {
        transition: background-color 0.15s ease-in-out;

        &:hover {
          background-color: #f8f9fa;
        }
      }

      td {
        padding: 0.75rem 0.5rem;
        position: relative;
        overflow: visible !important;
      }
    }
  }

  .info-icon {
    display: inline-block;
    font-style: normal;
    font-variant: normal;
    text-rendering: auto;
    line-height: 1;
    color: #6c757d;
    cursor: help;
    font-size: 0.85rem;
    opacity: 0.7;
    transition: opacity 0.2s ease-in-out;

    &:hover {
      opacity: 1;
      color: #007bff;
    }
  }

  .has-tooltip {
    position: relative;
    display: inline-block;
    cursor: help;
  }
}

:global(.ghost) {
  opacity: 0.5;
  background: #c8ebfb;
}

.timeline-event-group {
  &.is-dragging {
    opacity: 0.5;
  }
}

.captions-row {
  background-color: #fafbfc !important;

  &:hover {
    background-color: #fafbfc !important;
  }

  td {
    padding: 0 !important;
    border-top: none !important;
  }

  .captions-section {
    background-color: #fff;
    border-top: 1px solid #dee2e6;
    padding: 0.75rem 1rem;
    animation: slideDown 0.2s ease-out;

    .captions-table-wrapper {
      .table {
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        background-color: #fff;
        border: 1px solid #dee2e6;

        thead {
          background-color: #f8f9fa;

          th {
            font-weight: 600;
            font-size: 0.8rem;
            padding: 0.5rem;
            border-color: #dee2e6;
            color: #495057;

            &.col-time-range {
              width: 25%;
            }

            &.col-text {
              width: 65%;
            }

            &.col-actions {
              width: 10%;
              text-align: center;
            }
          }
        }

        tbody {
          tr {
            &:hover {
              background-color: #f8f9fa;
            }
          }

          td {
            padding: 0.5rem;
            vertical-align: middle;
            border-color: #dee2e6;

            .time-range-cell {
              gap: 0.25rem;

              .time-input-mini {
                font-family: 'Courier New', monospace;
                font-size: 0.8rem;
                font-weight: 500;
                text-align: center;
                min-width: 70px;
                padding: 0.375rem 0.5rem;
              }

              .mx-1 {
                font-size: 0.875rem;
                color: #6c757d;
              }
            }

            input[type="text"] {
              font-size: 0.875rem;
              padding: 0.375rem 0.75rem;
            }

            textarea {
              font-size: 0.875rem;
              padding: 0.375rem 0.75rem;
              resize: vertical;
              min-height: 60px;
            }
          }
        }
      }

      .btn-outline-primary {
        font-size: 0.875rem;
        padding: 0.375rem 0.75rem;
      }
    }
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 1000px;
    transform: translateY(0);
  }
}
</style>

<style lang="scss">
/* display:contents makes the div invisible for layout, so tbody elements are direct table children */
.draggable-container {
  display: contents;
}

.has-tooltip {
  &::before,
  &::after {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    z-index: 1050;
  }

  // Tooltip bubble
  &::before {
    content: attr(data-tooltip);
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: normal;
    white-space: normal;
    max-width: 250px;
    min-width: 150px;
    line-height: 1.4;
    text-align: left;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  // Tooltip arrow
  &::after {
    content: '';
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #333;
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
    visibility: visible;
  }
}
</style>